import { useState } from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Compact date picker for HidroVerse forms.
 *
 * Shows the date in `dd/MM/yyyy` (en-GB) but emits the canonical
 * `yyyy-MM-dd` string back through `onChange` so backend code keeps
 * receiving ISO dates. Click the calendar icon to pop a day picker.
 */
export default function DatePickerInput({
  value,            // canonical "yyyy-MM-dd" string (or "")
  onChange,         // (iso: string) => void
  placeholder = "dd/mm/yyyy",
  className = "",
  testId,
  disabled = false,
  minDate,
  maxDate,
}) {
  const [open, setOpen] = useState(false);

  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const display = parsed && isValid(parsed) ? format(parsed, "dd/MM/yyyy") : "";

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        readOnly
        value={display}
        placeholder={placeholder}
        onClick={() => !disabled && setOpen(true)}
        className="hv-input text-xs !h-8 w-full pr-9 cursor-pointer"
        data-testid={testId}
        disabled={disabled}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded hover:bg-white/10 flex items-center justify-center text-[#1FB6C1]"
            aria-label="Open calendar"
            data-testid={testId ? `${testId}-trigger` : undefined}
          >
            <CalendarDays className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="z-[100] p-0 w-auto bg-[#121419] border border-white/10" align="end">
          <Calendar
            mode="single"
            selected={parsed}
            onSelect={(d) => {
              if (d) {
                onChange(format(d, "yyyy-MM-dd"));
                setOpen(false);
              }
            }}
            disabled={(d) => {
              if (minDate && d < minDate) return true;
              if (maxDate && d > maxDate) return true;
              return false;
            }}
            initialFocus
          />
          {value && (
            <div className="flex items-center justify-between border-t border-white/10 px-2 py-1.5">
              <button
                type="button"
                onClick={() => { onChange(""); setOpen(false); }}
                className="text-[10px] text-[#647087] hover:text-rose-300"
              >Clear</button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[10px] text-[#1FB6C1] hover:underline"
              >Done</button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
